import React, { useContext, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import { joinUrl } from "../../utils/url";
import {
    GATEWAY_BASE,
    OAUTH_TOKEN_PATH,
    OAUTH_CALLBACK_BASE_PATH,
} from "../../config/env";

type TokenResp = { expiresIn?: number };

export const OAuthCallback: React.FC<{}> = () => {
    const { primeFromAuth } = useContext(AuthContext);
    const location = useLocation();
    const navigate = useNavigate();
    const { provider } = useParams();

    useEffect(() => {
        const run = async () => {
            const qs = new URLSearchParams(location.search);
            const code = qs.get("code");
            const state = qs.get("state");
            const expectedState = sessionStorage.getItem("oauth_state");
            const verifier = sessionStorage.getItem("pkce_verifier") || "";

            // Basic param checks
            if (!code || !verifier || !provider) {
                navigate("/login", {
                    replace: true,
                    state: { oauthError: "Missing OAuth parameters" },
                });
                return;
            }
            if (expectedState && state !== expectedState) {
                navigate("/login", {
                    replace: true,
                    state: { oauthError: "State mismatch" },
                });
                return;
            }

            const redirectUri = `${window.location.origin}${OAUTH_CALLBACK_BASE_PATH}/${provider.toLowerCase()}`;

            const form = new URLSearchParams({
                grant_type: "authorization_code",
                code,
                code_verifier: verifier,
                redirect_uri: redirectUri,
                identity_provider: provider.toUpperCase(),
            });

            try {
                const resp = await fetch(joinUrl(GATEWAY_BASE, OAUTH_TOKEN_PATH), {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    credentials: "include",
                    body: form.toString(),
                });

                // Specific unauthorized case (user not in DB, etc.)
                if (resp.status === 401 || resp.status === 403) {
                    navigate("/login", {
                        replace: true,
                        state: { oauthError: "User is not authorized" },
                    });
                    return;
                }

                // Any other failure
                if (!resp.ok) {
                    navigate("/login", {
                        replace: true,
                        state: { oauthError: "SSO Login Failed" },
                    });
                    return;
                }

                // Success: server set cookies; start refresh schedule using expiresIn
                const data = (await resp.json()) as TokenResp;
                primeFromAuth(data?.expiresIn);
                navigate("/", { replace: true });
            } catch {
                navigate("/login", {
                    replace: true,
                    state: { oauthError: "Network error during SSO" },
                });
            } finally {
                // Clean up PKCE artifacts regardless of outcome
                sessionStorage.removeItem("pkce_verifier");
                sessionStorage.removeItem("oauth_state");
            }
        };

        run();
    }, [location.search, navigate, provider, primeFromAuth]);

    return <div>Completing sign in…</div>;
};
